import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Copy, Check, AlertTriangle, RefreshCw, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatApiErrorDetail } from "../lib/api";
import { formatMoney, formatCents } from "../lib/pricing";
import { isoToEtLongDate, isoToEtTime, prettyTime } from "../lib/time";
import ContactBlock from "../components/ContactBlock";

const MAX_POLLS = 20;
const POLL_MS = 1000;

export default function BookingConfirmation() {
  const { conf } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const cancelled = searchParams.get("cancelled") === "true";

  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [copied, setCopied] = useState(false);
  const pollCountRef = useRef(0);
  const timerRef = useRef(null);

  async function loadBooking() {
    try {
      const r = await api.get(`/bookings/by-confirmation/${conf}`);
      setBooking(r.data);
      setError("");
      return r.data;
    } catch (e) {
      setError(formatApiErrorDetail(e));
      return null;
    }
  }

  useEffect(() => {
    setLoading(true);
    loadBooking().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conf]);

  useEffect(() => {
    if (!sessionId || !booking) return;
    if (booking.booking_status === "confirmed" || booking.booking_status === "cancelled") return;
    if (pollCountRef.current >= MAX_POLLS) return;

    setPolling(true);

    async function tick() {
      pollCountRef.current += 1;
      try {
        const r = await api.get(`/bookings/checkout-status/${sessionId}`);
        const status = r.data?.booking_status;
        if (status === "confirmed" || status === "cancelled") {
          await loadBooking();
          setPolling(false);
          return;
        }
      } catch (_) { /* retry */ }
      if (pollCountRef.current < MAX_POLLS) {
        timerRef.current = setTimeout(tick, POLL_MS);
      } else {
        setPolling(false);
      }
    }

    timerRef.current = setTimeout(tick, POLL_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, booking?.booking_status]);

  function copyConf() {
    if (!booking?.confirmation_number) return;
    navigator.clipboard.writeText(booking.confirmation_number)
      .then(() => {
        setCopied(true);
        toast.success("Copied confirmation number");
        setTimeout(() => setCopied(false), 1600);
      })
      .catch(() => toast.error("Could not copy"));
  }

  if (loading) return <div className="hbe-container py-20 text-hbe-ink/55" data-testid="conf-loading">Loading…</div>;
  if (error) {
    return (
      <div className="hbe-container py-20" data-testid="conf-error">
        <h1 className="font-serif text-3xl">Reservation not found</h1>
        <p className="mt-3 text-hbe-ink/70">{error}</p>
        <Link to="/" className="hbe-btn-primary mt-6 inline-flex">Return home</Link>
      </div>
    );
  }

  const held = booking.booking_status === "pending_payment";
  const confirmed = booking.booking_status === "confirmed";
  const isCancelled = booking.booking_status === "cancelled" || cancelled;
  const pollTimedOut = held && pollCountRef.current >= MAX_POLLS && !polling;

  const dep = booking.deposit || {};
  const balanceOutstanding = Math.max(0, (dep.balance_due_cents || 0) - (dep.balance_paid_cents || 0));
  const balanceCollected = (dep.balance_paid_cents || 0) > 0 && balanceOutstanding === 0;

  return (
    <div className="hbe-container py-12 md:py-20" data-testid="confirmation-page">
      <div className="max-w-3xl mx-auto">
        {isCancelled ? (
          <div data-testid="conf-cancelled">
            <div className="inline-flex items-center gap-2 text-red-700">
              <AlertTriangle size={20} />
              <span className="hbe-eyebrow" style={{ color: "inherit" }}>Payment cancelled</span>
            </div>
            <h1
              className="mt-3 font-sans font-semibold text-4xl md:text-5xl"
              style={{ fontFamily: '"Inter Display", "Inter", ui-sans-serif, system-ui, sans-serif' }}
            >
              Payment cancelled.
            </h1>
            <p className="mt-3 text-hbe-ink/75 max-w-xl">
              No charges were made. Your reservation is no longer held. You can start a new booking anytime.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book/68-azimut" className="hbe-btn-primary" data-testid="conf-try-again">Try again</Link>
              <Link to="/" className="hbe-btn-ghost" data-testid="conf-home">Return home</Link>
            </div>
          </div>
        ) : (
          <>
            <div className="inline-flex items-center gap-2 text-hbe-accent">
              {confirmed ? <CheckCircle2 size={20} /> : <Loader2 size={20} className={polling ? "animate-spin" : ""} />}
              <span className="hbe-eyebrow" style={{ color: "inherit" }}>
                {confirmed ? "Reservation confirmed" : polling ? "Finalising deposit…" : "Reservation held"}
              </span>
            </div>
            <h1
              className="mt-3 font-sans font-semibold text-4xl md:text-5xl"
              style={{ fontFamily: '"Inter Display", "Inter", ui-sans-serif, system-ui, sans-serif' }}
              data-testid="conf-status-title"
            >
              {confirmed ? "Reservation confirmed." : polling ? "Almost there…" : "Reservation held."}
            </h1>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <span className="text-hbe-ink/75">Confirmation number:</span>
              <button
                type="button"
                onClick={copyConf}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-hbe-soft border border-hbe-line font-medium text-hbe-primary hover:bg-hbe-mid/20 transition-colors"
                title="Copy"
                data-testid="conf-copy-btn"
              >
                <span data-testid="conf-number">{booking.confirmation_number}</span>
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>

            {polling && (
              <div className="mt-6 bg-hbe-soft/70 border border-hbe-mid/30 rounded-lg p-5 text-sm text-hbe-ink/85 flex items-start gap-3" data-testid="conf-polling-note">
                <Loader2 size={16} className="animate-spin mt-0.5 shrink-0" />
                <div>Confirming your deposit with Stripe. This usually takes a couple of seconds — please don't close this tab.</div>
              </div>
            )}

            {pollTimedOut && (
              <div className="mt-6 bg-amber-50 border border-amber-300 rounded-lg p-5 text-sm text-amber-900 flex items-start gap-3" data-testid="conf-timeout-note">
                <RefreshCw size={16} className="mt-0.5 shrink-0" />
                <div>
                  Your deposit is being processed. If you don't see confirmation within a few minutes,
                  please get in touch and quote confirmation <strong>{booking.confirmation_number}</strong>.
                </div>
              </div>
            )}

            {confirmed && (
              <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-lg p-5 text-sm text-emerald-900" data-testid="conf-success-note">
                HBE Entertainment will contact you with the exact marina and departure details shortly.
                Please have your balance ready on the day of your charter — cash or any payment method you've arranged.
              </div>
            )}

            {/* Price breakdown card */}
            {dep.total_cents > 0 && (
              <div className="mt-8 hbe-card p-6 space-y-3 text-sm" data-testid="conf-price-breakdown">
                <div className="hbe-eyebrow">Payment breakdown</div>

                {!balanceCollected && (
                  <div
                    className="space-y-1 pt-1"
                    data-testid="conf-deposit-explainer"
                  >
                    <p
                      className="leading-relaxed"
                      style={{ color: "#557A95", fontWeight: 400 }}
                    >
                      Your charter total is {formatCents(dep.total_cents)}.
                    </p>
                    <p
                      className="leading-relaxed"
                      style={{ color: "#557A95", fontWeight: 400 }}
                    >
                      You paid {formatCents(dep.deposit_amount_cents)} today
                      as a{" "}
                      <span className="font-semibold">non-refundable deposit</span>{" "}
                      — this locked your yacht.
                    </p>
                    <p
                      className="leading-relaxed"
                      style={{ color: "#557A95", fontWeight: 400 }}
                    >
                      The remaining {formatCents(balanceOutstanding)} is due
                      on the day of your charter (cash or another arrangement
                      with our team).
                    </p>
                    <p
                      className="mt-1 text-base leading-relaxed"
                      style={{ color: "#062843", fontWeight: 600 }}
                      data-testid="conf-deposit-math-line"
                    >
                      {formatCents(dep.deposit_amount_cents)} today +{" "}
                      {formatCents(balanceOutstanding)} on charter day ={" "}
                      {formatCents(dep.total_cents)} total.
                    </p>
                  </div>
                )}

                <div className="flex justify-between pt-3 border-t border-hbe-line text-hbe-ink/80">
                  <span>Total charter price</span>
                  <span data-testid="conf-total">{formatCents(dep.total_cents)}</span>
                </div>
                <div className="flex justify-between text-hbe-primary">
                  <span className="inline-flex items-center gap-1">
                    <Check size={14} /> Deposit paid (non-refundable)
                  </span>
                  <span data-testid="conf-deposit-paid">−{formatCents(dep.deposit_amount_cents)}</span>
                </div>
                <div className="border-t border-hbe-line pt-3 flex justify-between">
                  <span className="font-medium">
                    {balanceCollected ? "Balance collected" : "Balance due on charter day"}
                  </span>
                  <span
                    className={`font-sans font-semibold text-xl ${balanceCollected ? "text-emerald-700" : "text-hbe-primary"}`}
                    data-testid="conf-balance-due"
                  >
                    {balanceCollected ? "Paid" : formatCents(balanceOutstanding)}
                  </span>
                </div>
              </div>
            )}

            <div className="mt-8 hbe-card overflow-hidden">
              {booking.yacht?.images?.[0] && (
                <img
                  src={assetUrl(booking.yacht.images[0])}
                  alt={`${booking.yacht.name} — booked yacht`}
                  className="w-full aspect-[16/8] object-cover"
                  loading="lazy"
                />
              )}
              <div className="p-6 md:p-8 grid gap-6 md:grid-cols-2">
                <Info label="Yacht" value={booking.yacht?.name || "—"} testid="conf-yacht" />
                <Info label="Date" value={isoToEtLongDate(booking.start_datetime)} testid="conf-date" />
                <Info label="Start (ET)" value={prettyTime(isoToEtTime(booking.start_datetime))} testid="conf-start" />
                <Info label="End (ET)" value={prettyTime(isoToEtTime(booking.end_datetime))} testid="conf-end" />
                <Info label="Duration" value={`${booking.duration_hours} hours`} testid="conf-duration" />
                <Info label="Guests" value={String(booking.guest_count)} testid="conf-guests" />
                <Info label="Name" value={booking.customer_name} testid="conf-name" />
                <Info label="Total price" value={formatMoney(booking.price_total, booking.currency)} testid="conf-total-inline" />
              </div>
            </div>
          </>
        )}

        <div className="mt-10">
          <div className="hbe-eyebrow">Need to reach us?</div>
          <div className="mt-3"><ContactBlock /></div>
        </div>

        <div className="mt-14">
          <Link to="/" className="hbe-btn-ghost" data-testid="conf-back-home">Back home</Link>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, testid }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-hbe-ink/55">{label}</div>
      <div className="mt-1 text-hbe-ink" data-testid={testid}>{value}</div>
    </div>
  );
}
